<template>
  <v-card class="pa-4">
    <v-card-title class="d-flex align-center mt-2 mb-2 ">
      <v-icon class="mr-2">mdi-check-circle</v-icon>
      <h3 class="mb-0">
        Validar items - <strong>#{{ selectedOrder?.code }}</strong>
      </h3>
    </v-card-title>
    <v-card-text>
      <v-alert color="#D0EBFF" class="mb-4">
        <div class="d-flex justify-space-between align-center mb-0">
          <p class="mb-0 text-caption" style="font-size: 1rem !important;">
            Cliente: {{ selectedOrder?.client.name }}
          </p>
          <v-chip variant="outlined" size="small">
            <p class="mb-0 text-caption" style="font-size: 1rem !important;"><strong>LPN: {{ currentLpn }}</strong></p>
          </v-chip>
        </div>
      </v-alert>

      <!-- Mensaje de validación automática -->
      <v-alert 
        v-if="selectedOrder?.client?.skip_item_validation === true" 
        color="success"
        variant="tonal"
        icon="mdi-check-circle"
        class="mb-4"
      >
        <div class="text-body-2">
          <strong>Validación automática activada</strong>
          <p class="mb-0 mt-1">
            Los items de este cliente se validaron automáticamente porque sus productos no requieren escaneo de código de barras.
          </p>
        </div>
      </v-alert>

      <div>
        <h5 class="font-weight-medium">Escanear item para validar</h5>
        <v-row dense>
          <v-col cols="12" md="12">

            <v-text-field v-model="scannedItem" :disabled="allItemsValidated" label="Escanear código de barras del item..." autocomplete="off"
              autofocus variant="filled" class="mt-3 input-validate-item" ref="scannedItem"
              @keyup.enter="handleValidateItem">
              <template #prepend-inner>
                <v-icon class="fa-solid fa-barcode mr-2" style="margin-top: 4px; cursor: pointer;">mdi-barcode</v-icon>
              </template>
            </v-text-field>

          </v-col>
        </v-row>
      </div>

      <div class="mb-4">
        <v-row dense>
          <v-col cols="12" md="12" class="pa-0 mt-3 mb-3">
            <div class="d-flex ga-1 flex-wrap">
              <v-chip v-for="pkg in packages" :key="pkg.id" @click="() => handleChangePackage(pkg.id)"
                :variant="currentPackageId === pkg.id ? 'flat' : 'outlined'"
                :color="currentPackageId === pkg.id ? 'primary' : 'default'" clearable>
                <h5 class="mb-0">

                  Bulto {{ pkg.id }}
                </h5>
              </v-chip>
            </div>
          </v-col>
          <v-col cols="12" md="12">
            <v-btn @click="handleAddNewPackage" :disabled="allItemsValidated" variant="outlined" block color="primary">
              <v-icon start>mdi-plus</v-icon>
              Agregar Nuevo Bulto
            </v-btn>
          </v-col>
        </v-row>
      </div>

      <div class="mb-4">
        <h5 class="font-weight-medium mb-3">Items del Pedido:</h5>
        <div class="space-y-3">
          <v-card v-for="item in selectedOrder?.items || []" :key="item.id" :class="[
            'transition-colors pa-2 mb-2',
            isItemCompleted(item) ? 'bg-green-50' : 'bg-grey-50'
          ]" :variant="isItemCompleted(item) ? 'tonal' : 'outlined'"
            :color="isItemCompleted(item) ? 'success' : undefined">
            <v-card-text class="py-3">
              <div class="d-flex align-center justify-space-between">
                <div class="d-flex align-center ga-3">

                  <div :class="[
                    'd-flex align-center justify-center',
                    'pa-1',
                    'rounded-sm'
                  ]" :style="{
                    backgroundColor: isItemCompleted(item) ? '' : '#E5E7EB',
                    width: '60px',
                    height: '60px'
                  }">
                    <v-icon :color="isItemCompleted(item) ? 'green' : 'grey-darken-1'" size="24">
                      {{ isItemCompleted(item) ? 'mdi-check-circle' : 'mdi-package' }}
                    </v-icon>
                  </div>

                  <div>
                    <div :class="[
                      'text-h5 font-weight-bold',
                      isItemCompleted(item) ? 'text-success-darken-2' : ''
                    ]">
                      <strong>{{ item.product_presentation.product.name }}</strong>
                    </div>
                    <p class="subtext-card mb-0">
                      SKU: {{ item.product_presentation.product.sku }}
                    </p>
                    <p v-if="item.product_presentation.product.tiene_control_por_lotes" class="subtext-card mb-0">
                      Lote: {{ item.lote || 'N/A' }}
                    </p>
                    <p v-if="item.product_presentation.product.tiene_control_por_serie" class="subtext-card mb-0">
                      Serie: {{ item.serie || 'N/A' }}
                    </p>
                    <p v-if="item.product_presentation.product.tiene_control_por_descriptivo_1" class="subtext-card mb-0">
                      {{ getAliasName('descriptivo_1') }}: {{ item.descriptivo_1 || 'N/A' }}
                    </p>
                    <p v-if="item.product_presentation.product.tiene_control_por_descriptivo_2" class="subtext-card mb-0">
                      {{ getAliasName('descriptivo_2') }}: {{ item.descriptivo_2 || 'N/A' }}
                    </p>
                    <p v-if="item.product_presentation.product.tiene_control_por_descriptivo_3" class="subtext-card mb-0">
                      {{ getAliasName('descriptivo_3') }}: {{ item.descriptivo_3 || 'N/A' }}
                    </p>
                    <p v-if="item.product_presentation.product.tiene_control_de_calidad" class="subtext-card mb-0">
                      Calidad: {{ item.quality?.name || 'N/A' }}
                    </p>
                    <p v-if="item.product_presentation.product.requiere_fecha_expiracion" class="subtext-card mb-0">
                      Fecha mínima de expiración requerida: {{ item.expiration_date || 'N/A' }}
                    </p>
                    <p v-if="item.product_presentation.product.storage_unit && 
                           item.product_presentation.product.storage_unit.id != productoService?.STORAGE_UNITS_ID" 
                       class="subtext-card mb-0">
                      Almacenamiento: {{ item.storage_units_amount || 'N/A' }}
                    </p>
                  </div>
                </div>
                <div class="text-right">
                  <div :class="[
                    'text-h5 font-weight-bold',
                    isItemCompleted(item) ? 'text-success' : 'text-high-emphasis'
                  ]">
                    {{ item.validated || 0 }}/{{ item.amount }}
                  </div>

                  <v-chip v-if="isItemCompleted(item)" color="success" variant="tonal" size="small" class="mt-1">
                    <v-icon start size="small">mdi-check-circle</v-icon>
                    <h6 class="mb-0"><strong>Completo</strong></h6>
                  </v-chip>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </div>
      </div>

      <div class="d-flex flex-column ga-2">

        <v-btn ref="continueButton" :disabled="!allItemsValidated" color="primary" @click="handleFinishValidation" @keydown.enter="handleFinishValidation" size="large" block>
          <v-icon start>mdi-package</v-icon>
          Continuar con Empaquetado
        </v-btn>
        <v-btn @click="$emit('back-step')" variant="outlined" color="#2e3d45" size="large" class="mt-2" block>
          <v-icon start>mdi-arrow-left</v-icon>
          Volver
        </v-btn>

      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import AliasMixin from "@/mixins/AliasMixin.vue";
import { productoService } from "@/libs/ws/productoService";

export default {
  name: 'ValidateItems',
  mixins: [AliasMixin],
  props: {
    selectedOrder: {
      type: Object,
      default: null
    },
    currentLpn: {
      type: String,
      default: ''
    },
    packages: {
      type: Array,
      default: () => []
    },
    currentPackageId: {
      type: Number,
      default: 1
    }
  },
  emits: ['item-validated', 'validation-finished', 'add-new-package', 'change-package', 'back-step'],
  data() {
    return {
      scannedItem: '',
      productoService: productoService
    }
  },
  computed: {
    allItemsValidated() {
      return this.selectedOrder?.items.every((item) => item.validated === item.amount) || false
    }
  },
  watch: {
    allItemsValidated(newValue) {
      if (newValue) {
        // Todos los items están validados, enfocar el botón continuar
        this.$nextTick(() => {
          if (this.$refs.continueButton && this.$refs.continueButton.$el) {
            this.$refs.continueButton.$el.focus();
          }
        });
      }
    }
  },
  methods: {

    handleAddNewPackage() {
      this.$emit('add-new-package');
    },
    handleChangePackage(packageId) {
      this.$emit('change-package', packageId);
    },
    handleValidateItem() {
      if (!this.scannedItem) return

      this.$emit('item-validated', this.scannedItem)
      this.scannedItem = ''

      this.$nextTick(() => {
        if (this.$refs.scannedItem) {
          this.$refs.scannedItem.focus();
        }
      });
    },
    handleFinishValidation() {
      this.$emit('validation-finished')
    },
    isItemCompleted(item) {
      return item.validated === item.amount
    },
    focusInput() {
      this.$nextTick(() => {
        if (this.$refs.scannedItem) {
          this.$refs.scannedItem.focus();
        }
      });
    },

  }
}
</script>

<style scoped>
.space-y-3>*+* {
  margin-top: 12px;
}
</style>